import * as React from "react";
import { useMappedState } from "redux-react-hook";

import {
  FilterParams,
  PaginatedParams,
  PaginatedData
} from "src/library/utils";
import Table, { ITableInstance } from "src/library/components/Tables/Table";
import { ListActionsList } from "src/core/lists/components/ListActionsList";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { adminListTableColumns } from "src/core/shared/components/tables/adminListTableColumns";
import type { IStore } from "src/core/shared/store";
import { PageProps } from "src/pages/Router/routes";
import {
  listsApi,
  ListListsParams,
  IList
} from "src/core/lists/api/lists.api";
import { defaultPagination } from "src/core/orders/store/orders.reducer";

const AdminListsPage: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const { history, terminology } = props;
  const [ listsResponse, getLists ] = listsApi<PaginatedData<IList>, ListListsParams>("GET_LISTS");
  const listsData = listsResponse.data;
  const isLoading = listsResponse.pending;

  const { role, erp } = useMappedState((store: IStore) => ({
    user: store.global.user,
    role: store.global.role,
    erp: store.global.settings && store.global.settings.erp.name
  }));

  const TableRef = React.useRef<ITableInstance<IList>>(null);

  const onFetchData = React.useCallback((filters: FilterParams<IList>, pagination: PaginatedParams<IList>) => {
    const params = {
      pagination,
      filters: filters || {}
    };

    getLists<ListListsParams>(params);
  }, []);

  // If the endpoint changes, ensure reloading the table.
  React.useEffect(() => {
    if (TableRef.current) {
      TableRef.current.reloadTable(true);
    }
  }, []);

  const reload = React.useCallback(() => {
    if (TableRef.current) {
      TableRef.current.reloadTable();
    }
  }, [ TableRef ]);

  return (
    <Layout {...props}>
      <MainContentWrapper fill>
        <Table
          tableId="global"
          ref={TableRef}
          tableOf={terminology.list.plural.text}
          pages={listsData ? listsData.pages : 0}
          totalRows={listsData ? listsData.count : 0}
          loading={isLoading}
          defaultPagination={defaultPagination}
          data={listsData ? listsData.result : []}
          onFetchData={onFetchData}
          error={listsResponse.error}
          mainAction={row => history.push(`/lists/${row.original.id}`)}
          columns={adminListTableColumns({
            ActionButton: row => {
              if (erp && role) {
                return (
                  <ListActionsList
                    erp={erp}
                    terminology={terminology}
                    role={role}
                    list={row.original}
                    reload={reload}
                  />
                );
              }

              return null;
            },
            terminology
          })}
        />
      </MainContentWrapper>
    </Layout>
  );
};

export default AdminListsPage;
